import React, {
	createContext,
	useContext,
	useState,
	useRef,
	ReactNode,
	useMemo,
	useCallback,
	useEffect,
} from 'react'
import {
	UploadProgressPopup,
	MoveCopyProgressPopup,
	DownloadProgressPopup,
} from '../components/modals/workspace'
import type { UploadNodeType, UploadStatus } from '../lib/utils/upload'

export type UploadFileStatus = {
	file?: File // Optional for folder statuses
	progress: number
	storageKey: string
	status: UploadStatus
	currentFolderId: string
	abortController?: AbortController
	originalFileName?: string
	id?: string
	fileId?: string
	folderPath?: string
	priority?: number
	error?: string
	isFolder?: boolean
	folderName?: string
	filesCount?: number
	uploadedFilesCount?: number
	nodeType?: UploadNodeType
	parentStatusId?: string
	childrenIds?: string[]
	relativePath?: string
	depth?: number
	createdOnServer?: boolean
	sessionId?: string
}

export type UploadDiscoveryState = {
	isDiscovering: boolean
	rootItemsCount: number
	processedFiles: number
	totalFiles: number
}

type UploadQueueTask = {
	task: () => Promise<void>
	cancel: () => void
}

type QueuedUploadTask = {
	task: UploadQueueTask
	resolve: () => void
}

export type MoveCopyItemStatus = {
	id: string
	name: string
	type: number
	status: 'pending' | 'processing' | 'completed' | 'failed'
	error?: string
	operationId?: string
	operationType?: 'copy' | 'move'
	parentId?: string
	sourceFolderId?: string
}

export type DownloadItemStatus = {
	id: string
	name: string
	type: number
	status: 'pending' | 'processing' | 'completed' | 'failed' | 'canceled'
	error?: string
	progress?: number
	taskId?: string
}

interface ProgressContextType {
	// upload
	uploadStatuses: UploadFileStatus[]
	setUploadStatuses: React.Dispatch<React.SetStateAction<UploadFileStatus[]>>
	uploadDiscoveryState: UploadDiscoveryState
	setUploadDiscoveryState: React.Dispatch<
		React.SetStateAction<UploadDiscoveryState>
	>
	isUploadProgressPopupOpen: boolean
	setIsUploadProgressPopupOpen: (open: boolean) => void
	openUploadProgress: () => void
	enqueueUpload: (task: UploadQueueTask) => Promise<void>
	clearQueuedUploads: (cancelActive?: boolean) => void
	cancelUploadRef: React.MutableRefObject<((fileId: string) => void) | null>
	cancelAllUploadsRef: React.MutableRefObject<(() => void) | null>
	globalCancelFlagRef: React.MutableRefObject<boolean>
	canceledUploadStatusIdsRef: React.MutableRefObject<Set<string>>

	// move-copy
	moveCopyStatuses: MoveCopyItemStatus[]
	setMoveCopyStatuses: React.Dispatch<
		React.SetStateAction<MoveCopyItemStatus[]>
	>
	isMoveCopyProgressPopupOpen: boolean
	setIsMoveCopyProgressPopupOpen: (open: boolean) => void
	openMoveCopyProgress: () => void

	// download
	downloadStatuses: DownloadItemStatus[]
	setDownloadStatuses: React.Dispatch<
		React.SetStateAction<DownloadItemStatus[]>
	>
	isDownloadProgressPopupOpen: boolean
	setIsDownloadProgressPopupOpen: (open: boolean) => void
	openDownloadProgress: () => void
}

const ProgressContext = createContext<ProgressContextType | undefined>(
	undefined,
)

type FolderRefreshTarget = { folderId: string; refresh: () => void }
const UploadFolderRefreshContext = createContext<
	| {
			register: (target: FolderRefreshTarget) => () => void
			refresh: (folderId: string) => boolean
	  }
	| undefined
>(undefined)

const DownloadProgressContext = createContext<
	| Pick<
			ProgressContextType,
			| 'downloadStatuses'
			| 'setDownloadStatuses'
			| 'isDownloadProgressPopupOpen'
			| 'setIsDownloadProgressPopupOpen'
			| 'openDownloadProgress'
	  >
	| undefined
>(undefined)

export const useUploadFolderRefresh = () => {
	const context = useContext(UploadFolderRefreshContext)
	if (!context) throw new Error('ProgressProvider is required')
	return context
}

export const useDownloadProgress = () => {
	const context = useContext(DownloadProgressContext)
	if (!context) throw new Error('ProgressProvider is required')
	return context
}

export const ProgressProvider = ({ children }: { children: ReactNode }) => {
	// upload
	const [uploadStatuses, setUploadStatuses] = useState<UploadFileStatus[]>([])
	const [uploadDiscoveryState, setUploadDiscoveryState] =
		useState<UploadDiscoveryState>({
			isDiscovering: false,
			rootItemsCount: 0,
			processedFiles: 0,
			totalFiles: 0,
		})
	const [isUploadProgressPopupOpen, setIsUploadProgressPopupOpen] =
		useState(false)
	const cancelUploadRef = useRef<((fileId: string) => void) | null>(null)
	const cancelAllUploadsRef = useRef<(() => void) | null>(null)
	const globalCancelFlagRef = useRef<boolean>(false)
	const canceledUploadStatusIdsRef = useRef(new Set<string>())
	const uploadQueueRef = useRef<QueuedUploadTask[]>([])
	const isProcessingUploadQueueRef = useRef(false)
	const activeUploadQueueTaskRef = useRef<QueuedUploadTask | null>(null)

	// move-copy
	const [moveCopyStatuses, setMoveCopyStatuses] = useState<
		MoveCopyItemStatus[]
	>([])
	const [isMoveCopyProgressPopupOpen, setIsMoveCopyProgressPopupOpen] =
		useState(false)

	// download
	const [downloadStatuses, setDownloadStatuses] = useState<
		DownloadItemStatus[]
	>([])
	const [isDownloadProgressPopupOpen, setIsDownloadProgressPopupOpen] =
		useState(false)

	const activeUploads = uploadStatuses.some(
		(item) => item.status === 'pending' || item.status === 'uploading',
	)
	const hasActiveUploads = useCallback(() => activeUploads, [activeUploads])

	const activeDownloads = downloadStatuses.some(
		(item) => item.status === 'pending' || item.status === 'processing',
	)
	const hasActiveDownloads = useCallback(
		() => activeDownloads,
		[activeDownloads],
	)

	const activeMoveCopy = moveCopyStatuses.some(
		(item) => item.status === 'pending' || item.status === 'processing',
	)
	const hasActiveMoveCopy = useCallback(() => activeMoveCopy, [activeMoveCopy])

	useEffect(() => {
		if (!hasActiveUploads()) return

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault()
			event.returnValue = ''
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [hasActiveUploads])

	const openUploadProgress = useCallback(() => {
		const hasQueuedOrRunningUploads =
			isProcessingUploadQueueRef.current || uploadQueueRef.current.length > 0
		if (!hasActiveUploads() && !hasQueuedOrRunningUploads) {
			setUploadStatuses([])
			setUploadDiscoveryState({
				isDiscovering: false,
				rootItemsCount: 0,
				processedFiles: 0,
				totalFiles: 0,
			})
			canceledUploadStatusIdsRef.current.clear()
		}

		if (!hasActiveDownloads()) {
			setIsDownloadProgressPopupOpen(false)
			setDownloadStatuses([])
		}
		if (!hasActiveMoveCopy()) {
			setIsMoveCopyProgressPopupOpen(false)
			setMoveCopyStatuses([])
		}

		setIsUploadProgressPopupOpen(true)
	}, [hasActiveDownloads, hasActiveMoveCopy, hasActiveUploads])

	const processUploadQueue = useCallback(async () => {
		if (isProcessingUploadQueueRef.current) return

		isProcessingUploadQueueRef.current = true
		while (uploadQueueRef.current.length > 0) {
			const queuedTask = uploadQueueRef.current.shift()
			if (!queuedTask) continue

			activeUploadQueueTaskRef.current = queuedTask
			try {
				await queuedTask.task.task()
			} catch (error) {
				console.error('Upload queue task failed', error)
			} finally {
				activeUploadQueueTaskRef.current = null
				queuedTask.resolve()
			}
		}
		isProcessingUploadQueueRef.current = false
	}, [])

	const enqueueUpload = useCallback(
		(task: UploadQueueTask) =>
			new Promise<void>((resolve) => {
				uploadQueueRef.current.push({ task, resolve })
				void processUploadQueue()
			}),
		[processUploadQueue],
	)

	const clearQueuedUploads = useCallback((cancelActive = false) => {
		if (cancelActive) activeUploadQueueTaskRef.current?.task.cancel()
		uploadQueueRef.current.splice(0).forEach(({ task, resolve }) => {
			task.cancel()
			resolve()
		})
	}, [])

	const openDownloadProgress = useCallback(() => {
		setDownloadStatuses([])

		if (!hasActiveUploads()) {
			setIsUploadProgressPopupOpen(false)
			setUploadStatuses([])
		}
		if (!hasActiveMoveCopy()) {
			setIsMoveCopyProgressPopupOpen(false)
			setMoveCopyStatuses([])
		}

		setIsDownloadProgressPopupOpen(true)
	}, [hasActiveUploads, hasActiveMoveCopy])

	const openMoveCopyProgress = useCallback(() => {
		setMoveCopyStatuses([])

		if (!hasActiveUploads()) {
			setIsUploadProgressPopupOpen(false)
			setUploadStatuses([])
		}
		if (!hasActiveDownloads()) {
			setIsDownloadProgressPopupOpen(false)
			setDownloadStatuses([])
		}

		setIsMoveCopyProgressPopupOpen(true)
	}, [hasActiveUploads, hasActiveDownloads])

	const handleMobileBackdropClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (window.innerWidth >= 640) return

			if (e.target !== e.currentTarget) return

			if (isUploadProgressPopupOpen) {
				setUploadStatuses([])
				setIsUploadProgressPopupOpen(false)
			}
			if (isDownloadProgressPopupOpen) {
				setDownloadStatuses([])
				setIsDownloadProgressPopupOpen(false)
			}
			if (isMoveCopyProgressPopupOpen) {
				setMoveCopyStatuses([])
				setIsMoveCopyProgressPopupOpen(false)
			}
		},
		[
			isUploadProgressPopupOpen,
			isDownloadProgressPopupOpen,
			isMoveCopyProgressPopupOpen,
		],
	)

	// common state
	const isShow = useMemo(
		() =>
			isMoveCopyProgressPopupOpen ||
			isUploadProgressPopupOpen ||
			isDownloadProgressPopupOpen,
		[
			isMoveCopyProgressPopupOpen,
			isUploadProgressPopupOpen,
			isDownloadProgressPopupOpen,
		],
	)

	const folderRefreshTarget = useRef<FolderRefreshTarget | null>(null)
	// Keep access to the current folder even after the initiating uploader unmounts.
	const uploadFolderRefresh = useMemo(
		() => ({
			register: (target: FolderRefreshTarget) => {
				folderRefreshTarget.current = target
				return () => {
					if (folderRefreshTarget.current === target) {
						folderRefreshTarget.current = null
					}
				}
			},
			refresh: (folderId: string) => {
				if (folderRefreshTarget.current?.folderId === folderId) {
					folderRefreshTarget.current.refresh()
				}
				// An open Home page owns refresh decisions, including ignoring other folders.
				return folderRefreshTarget.current !== null
			},
		}),
		[],
	)
	const downloadProgress = useMemo(
		() => ({
			downloadStatuses,
			setDownloadStatuses,
			isDownloadProgressPopupOpen,
			setIsDownloadProgressPopupOpen,
			openDownloadProgress,
		}),
		[downloadStatuses, isDownloadProgressPopupOpen, openDownloadProgress],
	)

	return (
		<ProgressContext.Provider
			value={{
				// upload
				uploadStatuses,
				setUploadStatuses,
				uploadDiscoveryState,
				setUploadDiscoveryState,
				isUploadProgressPopupOpen,
				setIsUploadProgressPopupOpen,
				openUploadProgress,
				enqueueUpload,
				clearQueuedUploads,
				cancelUploadRef,
				cancelAllUploadsRef,
				globalCancelFlagRef,
				canceledUploadStatusIdsRef,

				// move-copy
				moveCopyStatuses,
				setMoveCopyStatuses,
				isMoveCopyProgressPopupOpen,
				setIsMoveCopyProgressPopupOpen,
				openMoveCopyProgress,

				// Download
				downloadStatuses,
				setDownloadStatuses,
				isDownloadProgressPopupOpen,
				setIsDownloadProgressPopupOpen,
				openDownloadProgress,
			}}
		>
			<UploadFolderRefreshContext.Provider value={uploadFolderRefresh}>
				<DownloadProgressContext.Provider value={downloadProgress}>
					{children}
				</DownloadProgressContext.Provider>
			</UploadFolderRefreshContext.Provider>

			{isShow && (
				<div
					className="fixed right-[10px] bottom-[10px] md:right-[20px] md:bottom-[20px] max-sm:left-[12px] max-sm:right-[12px] max-sm:inset-y-0 max-sm:pb-[12px] z-[1000] flex flex-col gap-[12px] items-end max-sm:items-stretch max-sm:justify-end"
					onClick={handleMobileBackdropClick}
				>
					<MoveCopyProgressPopup
						isOpen={isMoveCopyProgressPopupOpen}
						onClose={() => {
							setMoveCopyStatuses([])
							setIsMoveCopyProgressPopupOpen(false)
						}}
						items={moveCopyStatuses}
					/>

					<DownloadProgressPopup
						isOpen={isDownloadProgressPopupOpen}
						onClose={() => {
							setDownloadStatuses([])
							setIsDownloadProgressPopupOpen(false)
						}}
						items={downloadStatuses}
					/>

					<UploadProgressPopup
						isOpen={isUploadProgressPopupOpen}
						onClose={() => {
							clearQueuedUploads()
							canceledUploadStatusIdsRef.current.clear()
							globalCancelFlagRef.current = false
							setUploadStatuses([])
							setUploadDiscoveryState({
								isDiscovering: false,
								rootItemsCount: 0,
								processedFiles: 0,
								totalFiles: 0,
							})
							setIsUploadProgressPopupOpen(false)
						}}
						files={uploadStatuses}
						discoveryState={uploadDiscoveryState}
						onCancel={(fileId: string | null) =>
							fileId
								? cancelUploadRef.current?.(fileId)
								: cancelAllUploadsRef.current?.()
						}
					/>
				</div>
			)}
		</ProgressContext.Provider>
	)
}

export const useProgressContext = () => {
	const ctx = useContext(ProgressContext)
	if (!ctx) throw new Error('useProgress must be used within ProgressProvider')
	return ctx
}
